// pages/ReporteTSS.jsx
import React, { useState, useCallback } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';

const fmt = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const TIPO_OPTIONS = [
  { value: '01', label: '01 — Empleado privado (Ley 87-01)' },
  { value: '02', label: '02 — Empleado público' },
  { value: '03', label: '03 — Trabajador independiente' },
];

const filaVacia = () => ({
  _id:              Math.random().toString(36).slice(2),
  cedula:           '',
  nombre:           '',
  tipo:             '01',
  salario_cotizable: '',
  sfs_empleado:     '',
  afp_empleado:     '',
  sfs_patronal:     '',
  afp_patronal:     '',
  srl_patronal:     '',
  isr_retenido:     '',
  _manual:          true,
});

// Recalcula los campos TSS a partir del salario cotizable
const calcularCampos = (salario) => {
  const s = parseFloat(salario) || 0;
  return {
    sfs_empleado:  Math.round(s * 0.0304 * 100) / 100,
    afp_empleado:  Math.round(s * 0.0287 * 100) / 100,
    sfs_patronal:  Math.round(s * 0.0709 * 100) / 100,
    afp_patronal:  Math.round(s * 0.0710 * 100) / 100,
    srl_patronal:  Math.round(s * 0.0110 * 100) / 100,
  };
};

export default function ReporteTSS() {
  const hoy = new Date();
  const [mes,   setMes]   = useState(hoy.getMonth() + 1);
  const [anio,  setAnio]  = useState(hoy.getFullYear());
  const [datos, setDatos] = useState(null);
  const [filas, setFilas] = useState([]);   // filas editables (nómina + manuales)
  const [loading, setLoading] = useState(false);

  const anios = [];
  for (let y = hoy.getFullYear(); y >= hoy.getFullYear() - 3; y--) anios.push(y);

  // ── Cargar datos del servidor ─────────────────────────────────────────
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/nomina-universal/reporte-tss?mes=${mes}&anio=${anio}`);
      setDatos(data);
      // Cada fila de nómina se convierte en una fila editable
      setFilas(data.detalle.map(f => ({
        _id:              String(f.empleado_id),
        cedula:           f.cedula,
        nombre:           f.nombre,
        tipo:             '01',
        salario_cotizable: f.salario_cotizable,
        sfs_empleado:     f.sfs_empleado,
        afp_empleado:     f.afp_empleado,
        sfs_patronal:     f.sfs_patronal,
        afp_patronal:     f.afp_patronal,
        srl_patronal:     f.srl_patronal,
        isr_retenido:     f.isr_retenido,
        _manual:          false,
      })));
    } catch (e) {
      Swal.fire('Error', e.response?.data?.error || 'No se pudo cargar el reporte', 'error');
    } finally { setLoading(false); }
  }, [mes, anio]);

  // ── Edición de filas ──────────────────────────────────────────────────
  const editarFila = (id, campo, valor) => {
    setFilas(prev => prev.map(f => {
      if (f._id !== id) return f;
      const updated = { ...f, [campo]: valor };
      // Si cambia el salario cotizable, recalcular TSS automáticamente
      if (campo === 'salario_cotizable') {
        const calc = calcularCampos(valor);
        return { ...updated, ...calc };
      }
      return updated;
    }));
  };

  const agregarFila = () => setFilas(prev => [...prev, filaVacia()]);

  const eliminarFila = (id) => {
    setFilas(prev => prev.filter(f => f._id !== id));
  };

  // ── Totales en tiempo real ────────────────────────────────────────────
  const totales = filas.reduce((acc, f) => {
    acc.salario_cotizable += parseFloat(f.salario_cotizable || 0);
    acc.sfs_empleado      += parseFloat(f.sfs_empleado      || 0);
    acc.afp_empleado      += parseFloat(f.afp_empleado      || 0);
    acc.sfs_patronal      += parseFloat(f.sfs_patronal      || 0);
    acc.afp_patronal      += parseFloat(f.afp_patronal      || 0);
    acc.srl_patronal      += parseFloat(f.srl_patronal      || 0);
    acc.isr_retenido      += parseFloat(f.isr_retenido      || 0);
    return acc;
  }, { salario_cotizable: 0, sfs_empleado: 0, afp_empleado: 0, sfs_patronal: 0, afp_patronal: 0, srl_patronal: 0, isr_retenido: 0 });

  // ── Exportar TXT (descarga directa desde backend con datos de nómina) ─
  const exportarTxt = () => {
    const url = `${api.defaults.baseURL}/nomina-universal/reporte-tss/txt?mes=${mes}&anio=${anio}`;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    // Descarga con fetch para incluir el header de auth
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `TSS_Autodeterminacion_${anio}${String(mes).padStart(2,'0')}.txt`;
        a.click();
      })
      .catch(() => Swal.fire('Error', 'No se pudo exportar el TXT', 'error'));
  };

  // ── Exportar Excel (descarga directa desde backend) ───────────────────
  const exportarExcel = () => {
    const url = `${api.defaults.baseURL}/nomina-universal/reporte-tss/excel?mes=${mes}&anio=${anio}`;
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `TSS_Autodeterminacion_${anio}${String(mes).padStart(2,'0')}.xlsx`;
        a.click();
      })
      .catch(() => Swal.fire('Error', 'No se pudo exportar el Excel', 'error'));
  };

  // ── Estilos ───────────────────────────────────────────────────────────
  const st = {
    page:   { padding: '24px', fontFamily: 'sans-serif', maxWidth: 1400, margin: '0 auto' },
    card:   { background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px #0001', padding: 24, marginBottom: 20 },
    btn:    (bg, fg = '#fff') => ({ padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: bg, color: fg }),
    select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14 },
    input:  { padding: '5px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, width: '100%', boxSizing: 'border-box' },
    th:     { padding: '10px 8px', background: '#1a3a5c', color: '#fff', fontSize: 12, whiteSpace: 'nowrap', textAlign: 'left' },
    td:     { padding: '7px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 13 },
    tdNum:  { padding: '7px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 13, textAlign: 'right' },
    tfoot:  { padding: '9px 8px', fontWeight: 700, background: '#fff3cd', fontSize: 13, textAlign: 'right' },
  };

  return (
    <div style={st.page}>
      {/* Cabecera */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0 }}>🏥 Autodeterminación Mensual TSS</h2>
          <p style={{ margin: 0, color: '#888', fontSize: 14 }}>
            Reporte CNSS para subir al portal SUIR+ · Ley 87-01 · Tasas 2026
          </p>
        </div>
        <button style={st.btn('#6c757d')} onClick={() => window.history.back()}>← Volver</button>
      </div>

      {/* Selector mes/año + botón cargar */}
      <div style={{ ...st.card, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Mes</label>
          <select style={st.select} value={mes} onChange={e => setMes(parseInt(e.target.value))}>
            {MESES.map((nm, i) => <option key={i} value={i + 1}>{nm}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Año</label>
          <select style={st.select} value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {anios.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button style={st.btn('#0d6efd')} onClick={cargar} disabled={loading}>
          {loading ? 'Cargando...' : '🔍 Generar Reporte'}
        </button>

        {datos && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button style={st.btn('#28a745')} onClick={exportarExcel}>
              📊 Exportar Excel
            </button>
            <button style={st.btn('#6f42c1')} onClick={exportarTxt}>
              📄 Exportar TXT (SUIR+)
            </button>
          </div>
        )}
      </div>

      {/* Info del período cargado */}
      {datos && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Mes',               value: `${datos.mes_nombre} ${datos.anio}`,      color: '#0d6efd' },
            { label: 'Períodos incluidos', value: datos.periodos_incluidos,                  color: '#6f42c1' },
            { label: 'Empleados nómina',   value: datos.empleados,                           color: '#fd7e14' },
            { label: 'Empleados en tabla', value: filas.length,                              color: '#28a745' },
          ].map(k => (
            <div key={k.label} style={{ ...st.card, marginBottom: 0, padding: 14, borderLeft: `4px solid ${k.color}` }}>
              <div style={{ fontSize: 11, color: '#888' }}>{k.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabla editable */}
      {datos && (
        <div style={st.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h4 style={{ margin: 0 }}>Detalle por Empleado — {MESES[mes - 1]} {anio}</h4>
            <button style={st.btn('#17a2b8')} onClick={agregarFila}>
              + Agregar Empleado / Ingreso Adicional
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Cédula','Nombre','Tipo','Sal. Cotizable','SFS Emp\n3.04%','AFP Emp\n2.87%',
                    'SFS Pat\n7.09%','AFP Pat\n7.10%','SRL Pat\n1.10%','ISR Ret.',''].map((h, i) => (
                    <th key={i} style={st.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map(f => (
                  <tr key={f._id} style={{ background: f._manual ? '#f0fff4' : '#fff' }}>
                    <td style={st.td}>
                      <input style={st.input} value={f.cedula}
                        onChange={e => editarFila(f._id, 'cedula', e.target.value)} />
                    </td>
                    <td style={{ ...st.td, minWidth: 200 }}>
                      <input style={st.input} value={f.nombre}
                        onChange={e => editarFila(f._id, 'nombre', e.target.value)} />
                    </td>
                    <td style={st.td}>
                      <select style={{ ...st.input, width: 'auto' }} value={f.tipo}
                        onChange={e => editarFila(f._id, 'tipo', e.target.value)}>
                        {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value}</option>)}
                      </select>
                    </td>
                    <td style={st.td}>
                      <input style={{ ...st.input, width: 110, textAlign: 'right' }}
                        type="number" step="0.01" min="0"
                        value={f.salario_cotizable}
                        onChange={e => editarFila(f._id, 'salario_cotizable', e.target.value)} />
                    </td>
                    {['sfs_empleado','afp_empleado','sfs_patronal','afp_patronal','srl_patronal','isr_retenido'].map(col => (
                      <td key={col} style={st.td}>
                        <input style={{ ...st.input, width: 90, textAlign: 'right' }}
                          type="number" step="0.01" min="0"
                          value={f[col]}
                          onChange={e => editarFila(f._id, col, e.target.value)} />
                      </td>
                    ))}
                    <td style={st.td}>
                      <button
                        title="Eliminar fila"
                        onClick={() => eliminarFila(f._id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#dc3545' }}>
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ ...st.tfoot, textAlign: 'left' }}>TOTALES ({filas.length} empleados)</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.salario_cotizable)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.sfs_empleado)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.afp_empleado)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.sfs_patronal)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.afp_patronal)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.srl_patronal)}</td>
                  <td style={st.tfoot}>RD$ {fmt(totales.isr_retenido)}</td>
                  <td style={st.tfoot}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, color: '#888', lineHeight: 1.6 }}>
            💡 <strong>Filas en verde</strong> fueron agregadas manualmente y no están en la nómina del sistema.<br/>
            💡 Al cambiar el <strong>Salario Cotizable</strong> de una fila manual, los demás campos TSS se recalculan automáticamente.<br/>
            ⚠️ Los botones de exportar TXT/Excel descargan los datos <strong>de la nómina procesada</strong>. Para incluir filas manuales, usa <strong>Exportar Excel</strong>, agrega las filas ahí y luego genera el TXT desde la hoja editada.
          </div>
        </div>
      )}

      {!datos && !loading && (
        <div style={{ ...st.card, textAlign: 'center', color: '#aaa', padding: 48 }}>
          Selecciona el mes y año, luego haz clic en <strong>Generar Reporte</strong>
        </div>
      )}
    </div>
  );
}
